import { type ErrorInfo, type ReactNode, Component } from 'react'

import { captureException } from '@/domains/observability'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    captureException(error, { componentStack: info.componentStack })
    console.error(
      'Render error caught by boundary:',
      error,
      info.componentStack
    )
  }

  render() {
    if (!this.state.error) return this.props.children

    return (
      <div className='flex min-h-dvh items-center justify-center bg-page px-6 py-12'>
        <div className='w-full max-w-md text-center'>
          <p className='font-display text-[15px] tracking-[0.2em] text-ink-muted uppercase'>
            Something went wrong
          </p>
          <h1 className='mt-3 font-display text-[40px] leading-[1.1] tracking-[-0.5px] text-ink'>
            Well, this is embarrassing
          </h1>
          <p className='mt-4 text-[15px] leading-relaxed text-ink-soft'>
            We apologize for the inconvenience. Our engineering team is aware
            and working diligently to get this resolved.
          </p>

          <div className='mt-8'>
            <button
              type='button'
              onClick={() => window.location.reload()}
              className='w-full cursor-pointer rounded-xl bg-accent py-3.5 text-[15px] font-medium text-white transition-all hover:-translate-y-px hover:bg-[#15296b]'
            >
              Reload page
            </button>
          </div>
        </div>
      </div>
    )
  }
}
